import { PlayerAPI, PlayerEvent } from 'bitmovin-player';
import { SeekBar, UIInstanceManager } from 'bitmovin-player-ui';

/** Seconds behind the live edge that Live TV starts playback at (`playerReadyCallback` in useEpgHandler). */
export const TIMESHIFT_ON_PLAYER_READY = 5;

/** Drift allowed on top of that start offset before playback counts as behind live. */
const LIVE_EDGE_TOLERANCE = 5;

/**
 * Whether playback is at the live edge, counting the start offset as live: playing and less than 10s behind.
 * It doesn't check `isLive()`; callers that can get a VOD source must.
 *
 * TODO: bitmovin-player-ui's own rule (`PlaybackTimeLabel`) is stricter: at the live edge only when `getTimeShift()`
 * is not negative and the player isn't paused (a live stream without a timeshift window also counts while paused).
 * Live TV starts at -5s, so it never meets that rule and the "go live" button would start orange. Switch to
 * Bitmovin's rule if live playback starts at the edge.
 */
export const isNearLiveEdge = (player: PlayerAPI): boolean =>
    !player.isPaused() && Math.abs(player.getTimeShift()) < TIMESHIFT_ON_PLAYER_READY + LIVE_EDGE_TOLERANCE;

// The events bitmovin-player-ui re-checks the live edge on, plus Ready and TimeChanged for the initial state and drift
const LIVE_EDGE_EVENTS = [
    PlayerEvent.Ready,
    PlayerEvent.TimeChanged,
    PlayerEvent.TimeShift,
    PlayerEvent.TimeShifted,
    PlayerEvent.Playing,
    PlayerEvent.Paused,
    PlayerEvent.StallStarted,
    PlayerEvent.StallEnded,
];

/**
 * Calls `onChange` with whether a live stream is at the live edge, now and on every event that can change it.
 *
 * Meant for a UI component's `configure()`: the player it gets there is bitmovin-player-ui's wrapper, which removes
 * these handlers when the UI is released.
 */
export const subscribeToLiveEdge = (player: PlayerAPI, onChange: (atLiveEdge: boolean) => void) => {
    const update = () => onChange(player.isLive() && isNearLiveEdge(player));
    LIVE_EDGE_EVENTS.forEach((event) => player.on(event, update));
    update();
};

/** SeekBar that carries `bmpui-ui-seekbar-live-edge` while a live stream is at the live edge (see bitmovin-skin). */
export class LiveEdgeSeekBar extends SeekBar {
    configure(player: PlayerAPI, uimanager: UIInstanceManager, configureSeek = true): void {
        super.configure(player, uimanager, configureSeek);

        const liveEdgeCssClass = this.prefixCss('ui-seekbar-live-edge');
        subscribeToLiveEdge(player, (atLiveEdge) => {
            if (atLiveEdge) {
                this.getDomElement().addClass(liveEdgeCssClass);
            } else {
                this.getDomElement().removeClass(liveEdgeCssClass);
            }
        });
    }
}
